import React, { useState } from 'react';
import { Library, Play, Square, Download, Share2, Trash2, Search } from 'lucide-react';
import { AudioGeneration, UseCase } from '../../types';
import { WaveformVisualizer } from '../common/WaveformVisualizer';

interface AudioLibraryProps {
  generations: AudioGeneration[];
  onDeleteGeneration: (id: string) => void;
}

export const AudioLibrary: React.FC<AudioLibraryProps> = ({ generations, onDeleteGeneration }) => {
  const [selectedUseCase, setSelectedUseCase] = useState<'ALL' | UseCase>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeGenerationId, setActiveGenerationId] = useState<string | null>(
    generations[0]?.id || null,
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1.0);

  const filteredGenerations = generations.filter((gen) => {
    const matchesCategory = selectedUseCase === 'ALL' || gen.useCase === selectedUseCase;
    const matchesSearch =
      gen.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      gen.script.toLowerCase().includes(searchQuery.toLowerCase()) ||
      gen.voiceName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      gen.scripturesDetected.some((s) => s.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const activeGen = generations.find((g) => g.id === activeGenerationId);

  const togglePlay = (id: string) => {
    if (activeGenerationId === id && isPlaying) {
      setIsPlaying(false);
    } else {
      setActiveGenerationId(id);
      setIsPlaying(true);
    }
  };

  const getUseCaseBadge = (uc: UseCase) => {
    switch (uc) {
      case 'SERMON_RECAP':
        return 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/30';
      case 'DEVOTIONAL':
        return 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30';
      case 'PRAYER':
        return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
      case 'ANNOUNCEMENT':
        return 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
    }
  };

  return (
    <div className="space-y-8 w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2.5">
            <Library className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            Audio Library & Archive
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Stream, download, and manage your church's generated scripture recaps and devotionals.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-2.5" />
            <input
              type="text"
              placeholder="Search by Scripture, voice, title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-3.5 py-2 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-200 dark:placeholder-slate-500 transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Featured Master Audio Player Bar */}
      {activeGen && (
        <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm dark:bg-slate-900/90 dark:border-indigo-500/30 dark:shadow-xl space-y-4 transition-colors">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <button
                onClick={() => togglePlay(activeGen.id)}
                className="w-12 h-12 rounded-xl bg-gradient-to-tr from-indigo-600 to-amber-500 text-white flex items-center justify-center shadow-md shadow-indigo-500/20 hover:scale-105 transition-all flex-shrink-0 cursor-pointer"
              >
                {isPlaying ? (
                  <Square className="w-5 h-5 fill-current" />
                ) : (
                  <Play className="w-5 h-5 fill-current ml-0.5" />
                )}
              </button>

              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-slate-900 dark:text-white text-sm">{activeGen.title}</span>
                  <span
                    className={`text-[9px] uppercase font-bold px-2 py-0.5 rounded border ${getUseCaseBadge(
                      activeGen.useCase,
                    )}`}
                  >
                    {activeGen.useCase.replace('_', ' ')}
                  </span>
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-2 flex-wrap">
                  <span>Voice: <strong className="text-slate-900 dark:text-slate-200">{activeGen.voiceName}</strong></span>
                  <span>•</span>
                  <span>{activeGen.durationSeconds}s duration</span>
                  <span>•</span>
                  <span className="text-amber-600 dark:text-amber-400 font-semibold">🪙 {(activeGen.creditsBurned || 450).toLocaleString()} Credits</span>
                </div>
              </div>
            </div>

            {/* Playback Controls & Speed Selector */}
            <div className="flex items-center gap-3">
              {/* Speed Buttons */}
              <div className="flex items-center bg-slate-100 border border-slate-200 p-1 rounded-lg dark:bg-slate-950/80 dark:border-slate-800 text-[11px] font-mono font-bold">
                {[0.75, 1.0, 1.25, 1.5].map((s) => (
                  <button
                    key={s}
                    onClick={() => setPlaybackSpeed(s)}
                    className={`px-2 py-0.5 rounded transition-colors cursor-pointer ${
                      playbackSpeed === s
                        ? 'bg-indigo-600 text-white'
                        : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                    }`}
                  >
                    {s}x
                  </button>
                ))}
              </div>

              {/* Download & Share */}
              <a
                href={activeGen.audioUrl}
                download={`${activeGen.title}.mp3`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-white text-xs font-semibold dark:border-slate-700 transition-all shadow-sm"
              >
                <Download className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                <span>Download MP3</span>
              </a>

              <button
                onClick={() => alert('Streaming link copied to clipboard.')}
                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 dark:border-slate-700 transition-all cursor-pointer"
                title="Copy Stream URL"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Active Waveform Visualizer */}
          <div className="pt-2 border-t border-slate-200 dark:border-slate-800/80">
            <WaveformVisualizer isPlaying={isPlaying} barCount={54} height={40} />
          </div>
        </div>
      )}

      {/* Generations Table / List */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm dark:bg-slate-900/70 dark:border-slate-800 dark:shadow-xl overflow-hidden transition-colors">
        <div className="p-4 border-b border-slate-200 dark:border-slate-800/80 flex items-center justify-between">
          <div className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
            Audio Recordings ({filteredGenerations.length})
          </div>

          {/* Category Filter Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto">
            {['ALL', 'SERMON_RECAP', 'DEVOTIONAL', 'PRAYER', 'ANNOUNCEMENT'].map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedUseCase(cat as any)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all cursor-pointer ${
                  selectedUseCase === cat
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200'
                }`}
              >
                {cat.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        <div className="divide-y divide-slate-100 dark:divide-slate-800/80">
          {filteredGenerations.map((gen) => {
            const isThisPlaying = activeGenerationId === gen.id && isPlaying;

            return (
              <div
                key={gen.id}
                className={`p-4 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                  activeGenerationId === gen.id
                    ? 'bg-indigo-50/50 dark:bg-indigo-950/20'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/30'
                }`}
              >
                <div className="flex items-start gap-3.5 flex-1">
                  <button
                    onClick={() => togglePlay(gen.id)}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all flex-shrink-0 mt-0.5 cursor-pointer ${
                      isThisPlaying
                        ? 'bg-amber-500 text-white'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300'
                    }`}
                  >
                    {isThisPlaying ? (
                      <Square className="w-3.5 h-3.5 fill-current" />
                    ) : (
                      <Play className="w-3.5 h-3.5 fill-current ml-0.5" />
                    )}
                  </button>

                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-bold text-xs text-slate-900 dark:text-white">{gen.title}</h4>
                      <span
                        className={`text-[9px] uppercase font-bold px-2 py-0.2 rounded border ${getUseCaseBadge(
                          gen.useCase,
                        )}`}
                      >
                        {gen.useCase.replace('_', ' ')}
                      </span>
                    </div>

                    <p className="text-[11px] text-slate-600 dark:text-slate-400 line-clamp-1 leading-relaxed">
                      {gen.script}
                    </p>

                    {/* Metadata Badges */}
                    <div className="flex items-center gap-3 text-[10px] text-slate-500 font-medium pt-0.5 flex-wrap">
                      <span>Voice: {gen.voiceName}</span>
                      <span>•</span>
                      <span>{gen.characterCount} chars</span>
                      <span>•</span>
                      <span>{gen.durationSeconds}s</span>
                      <span>•</span>
                      <span className="text-amber-600 dark:text-amber-400 font-semibold">
                        🪙 {(gen.creditsBurned || 450).toLocaleString()} Credits
                      </span>
                      {gen.scripturesDetected.length > 0 && (
                        <>
                          <span>•</span>
                          <span className="text-indigo-600 dark:text-indigo-400 font-semibold">
                            {gen.scripturesDetected.join(', ')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 self-end sm:self-auto">
                  <a
                    href={gen.audioUrl}
                    download={`${gen.title}.mp3`}
                    className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 dark:border-slate-700 transition-all"
                    title="Download Audio"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </a>

                  <button
                    onClick={() => onDeleteGeneration(gen.id)}
                    className="p-2 rounded-lg bg-slate-100 hover:bg-rose-50 hover:text-rose-600 text-slate-400 border border-slate-200 dark:bg-slate-800 dark:hover:bg-rose-950 dark:hover:text-rose-400 dark:border-slate-700 transition-all cursor-pointer"
                    title="Delete Audio"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
